/**
 * GrandHotel PMS — Merkezi API Servisleri
 *
 * Tüm backend API çağrıları bu dosyada tanımlıdır.
 * Axios instance'ı (axiosConfig.ts) kullanılır.
 *
 * Kullanım:
 *   import { roomsApi, guestsApi } from '../api/services';
 *   const rooms = await roomsApi.getAll();
 */

import api from './axiosConfig';
import type { components } from './generated/schema';
import type { ShiftHandover } from '../utils/constants';

/**
 * Backend OpenAPI şemasından üretilen tipler (src/api/generated/schema.ts).
 * Elle düzenlenmez — backend değişince: npm run gen:api
 */
type Schemas = components['schemas'];

/* ==================== TİPLER ==================== */

/** Backend RoomSerializer çıktısı */
export interface ApiRoom {
  id: number;
  roomNumber: string;
  bedType: string;
  floor: number;
  capacity: number;
  view: string;
  price: string;
  status: string;
  guestName: string | null;
  guests: ApiRoomGuest[];
  reservationId: number | null;
  reservationNotes: string | null;
  reservationCheckIn: string | null;
  reservationCheckOut: string | null;
  reservationStaffName: string | null;
  reservationStatus: string | null;
  reservationOwnerName: string | null;
  reservationCompanyId: number | null;
  reservationAgencyId: number | null;
  reservationAgencyName: string | null;
  reservationAgencyCode: string | null;
  /** Konaklayan müşterinin folio borcu (harcama − ödenen); konaklama yoksa null */
  folioBalance: string | null;
  beds: { type: string }[];
  notes: string | null;
  minibar?: ApiRoomMinibarItem[];
}

export interface ApiRoomMinibarItem {
  productId: number;
  productName: string;
  placed: number;
  remaining: number;
  consumed: number;
  price: string;
}

export interface ApiStockItem {
  id: number;
  name: string;
  category: string;
  unit: string;
  quantity: number;
  isMinibar: boolean;
  minibarPrice?: string | null;
  createdAt: string;
}

export interface ApiMinibarProduct {
  id: number;
  name: string;
  price: string;
  unit: string;
  totalStock: number;
  inMinibar: number;
  sold: number;
  availableStock: number;
}

export interface ApiMinibarRoomItem {
  id: number;
  roomId: number;
  roomNumber: string;
  productId: number;
  productName: string;
  quantity: number;
  price: string;
  placedBy: string;
  placedAt: string;
}

export interface ApiRoomGuest {
  /** null = henüz Guest yaratılmamış placeholder rezervasyon misafiri */
  guestId: number | null;
  guestName: string;
  phone: string;
  checkIn: string | null;
  checkOut: string | null;
  isActive: boolean;
}

export interface ApiGuest {
  id: number;
  tcNo: string;
  firstName: string;
  lastName: string;
  phone: string;
  email?: string | null;
  companyId?: number | null;
  isBlocked?: boolean;
  createdAt?: string;
}

export interface ApiCompany {
  id: number;
  name: string;
  taxNumber: string | null;
  address: string | null;
  phone: string | null;
  email: string | null;
  agreedRate?: string | number | null;
  accountBalance?: string | null;
}

export interface ApiAgency {
  id: number;
  name: string;
  taxNumber: string | null;
  address: string | null;
  phone: string | null;
  email: string | null;
  contactPerson: string | null;
  commissionRate: string | number | null;
  notes: string | null;
  isActive: boolean;
  accountBalance?: string | null;
}

export interface ApiAccountTransaction {
  id: number;
  type: 'debit' | 'credit';
  amount: string;
  description: string;
  status: 'open' | 'settled';
  companyId: number | null;
  agencyId: number | null;
  guestId: number | null;
  targetType: 'company' | 'agency' | 'guest' | null;
  targetName: string;
  sourceReservationId: number | null;
  sourceFolioItemId: number | null;
  settledByFolioId: number | null;
  settledAt: string | null;
  /** Borçta tahsil edilen, alacakta borçlara dağıtılan kısım */
  settledAmount: string;
  /** amount − settledAmount (borçta kalan borç, alacakta kullanılmamış avans) */
  remaining: string;
  /** Tahsilat kayıtlarında: cash | card | transfer | other */
  paymentMethod: string;
  createdAt: string;
  createdBy: string;
  guestName: string;
  roomNumber: string;
  reservationCheckIn: string | null;
  reservationCheckOut: string | null;
}

/** Firma / acente borçlular satırı — cari bakiyesi > 0 olanlar (cari defter) */
export interface CariDebtorRow {
  id: number;
  name: string;
  taxNumber: string | null;
  phone: string | null;
  email: string | null;
  commissionRate?: string;
  /** Cari bakiye (Σ borç − Σ alacak) */
  balance: string;
  openDebit: string;
  unallocatedCredit: string;
  openCount: number;
  /** Konaklayan (check-in'li) rezervasyonların açık folio bakiyesi — henüz cariye aktarılmadı */
  inHouseBalance: string;
  activeReservations: number;
}

/** Sayfalı liste yanıtı (backend config/pagination.py — ?page= ile) */
export interface Paginated<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

/** Sayfalı liste sorgusu (page 1'den başlar) — hooks/useServerTable */
export interface PageQuery {
  page: number;
  pageSize: number;
  search?: string;
}

export interface CollectPayload {
  companyId?: number;
  agencyId?: number;
  guestId?: number;
  amount: number;
  paymentMethod: string;
  description?: string;
  /** Borç kutucuklarından seçilenler: önce bunlar kapanır, tutar bunların kalanını aşamaz */
  debitIds?: number[];
}

export type CariTargetType = 'company' | 'agency' | 'guest';

/** Borçlular listesi satırı — kişi / firma / acente başına tek satır (backend ledger.debtor_rows) */
export interface CariDebtorPartyRow {
  targetType: CariTargetType;
  id: number;
  name: string;
  phone: string;
  taxNumber: string;
  balance: string;
  openDebit: string;
  unallocatedCredit: string;
  /** Açık borç kutucuğu (konaklama) sayısı */
  openCount: number;
  oldestOpenAt: string | null;
}

export interface CariDebtorsResponse {
  summary: { count: number; totalBalance: string; totalOpenDebit: string; openCount: number };
  items: CariDebtorPartyRow[];
}

/** Borç kutucuğu — bir konaklamanın açık borcu (backend ledger.open_debts) */
export interface CariDebtCard {
  key: string;
  reservationId: number | null;
  roomNumber: string;
  guestNames: string;
  checkIn: string | null;
  checkOut: string | null;
  nights: number;
  amount: string;
  settled: string;
  remaining: string;
  debitIds: number[];
  descriptions: string[];
  firstDebitAt: string;
  /** Bu borca yapılmış tahsilatlar (kısmi ödemeler) */
  payments: { date: string; amount: string; paymentMethod: string; description: string; isTransfer: boolean }[];
  folio: {
    items: { id: number; date: string | null; category: string; description: string; amount: string; paymentMethod: string }[];
    /** Kategori → toplam (room_charge, minibar, discount, payment, account_transfer_debit …) */
    byCategory: Record<string, string>;
  } | null;
}

export interface CariOpenDebtsResponse {
  party: { targetType: CariTargetType; id: number; name: string; phone: string; taxNumber: string };
  summary: { balance: string; openDebit: string; unallocatedCredit: string; count: number };
  debts: CariDebtCard[];
}

export interface AccountTransactionDebtorListResponse {
  summary: {
    debitTotal: string;
    creditTotal: string;
    netBalance: string;
    count: number;
  };
  items: ApiAccountTransaction[];
}

export interface ApiReservation {
  id: number;
  roomId: number;
  roomNumber: string;
  companyId: number | null;
  companyName: string | null;
  agencyId?: number | null;
  agencyName?: string | null;
  agencyReservationCode?: string | null;
  checkIn: string;
  checkOut: string | null;
  guestNames: string | null;
  guestCount: number;
  status: string;
  notes: string;
  totalAmount: string;
  paidAmount: string;
  isActive: boolean;
  createdByStaff: string | null;
  /** Hızlı rezervasyonda kalıcı Guest oluşmaz; geçici isim/telefon burada */
  placeholderGuestName?: string;
  placeholderGuestPhone?: string;
}

export interface ApiReservationDetail extends ApiReservation {
  stays: ApiStay[];
  folioItems: ApiFolioItem[];
}

export interface ApiStay {
  id: number;
  guestId: number;
  guestName: string;
  phone: string;
  checkIn: string;
  checkOut: string | null;
  isActive: boolean;
}

export interface ApiFolioItem {
  id: number;
  reservationId: number;
  guestId?: number | null;
  category: string;
  description: string;
  amount: number | string;
  date: string;
  paymentMethod?: string;
  /** manual | checkout | night_audit | minibar | restaurant */
  origin?: string;
  createdBy?: string | null;
  createdAt?: string;
}

export interface ApiHotelDocument {
  id: number;
  docType: string;
  name: string;
  url: string;
  uploadedAt: string;
}

export interface ApiHotelImage {
  id: number;
  name: string;
  url: string;
  uploadedAt: string;
}

export interface ApiHotel {
  id: number | null;
  name: string;
  address: string;
  phone: string;
  email: string;
  taxNumber: string;
  status: string;
  rejectionReason: string;
  roomCount: number;
  businessLicense: ApiHotelDocument | null;
  taxCertificate: ApiHotelDocument | null;
  tourismLicense: ApiHotelDocument | null;
  images: ApiHotelImage[];
  logo: string | null;
  requirePaymentAtCheckin?: boolean;
  companyExemptFromCheckinPayment?: boolean;
  serviceOpen?: boolean;
  menuAccessToken?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ApiKbsResult {
  guestName: string;
  tcNo: string;
  status: 'checked_in' | 'failed';
  kbsReference: string;
  message: string;
}

export interface ApiKbsSendResponse {
  roomNumber: string;
  results: ApiKbsResult[];
}

export interface ApiKbsRecord {
  id: number;
  roomId: number;
  roomNumber: string;
  guestName: string;
  tcNo: string;
  status: string;
  systemType?: 'egm' | 'jandarma';
  kbsReference: string;
  sentAt: string;
  checkoutAt: string | null;
  createdAt: string;
}

export interface ApiKbsSettings {
  systemType: 'egm' | 'jandarma';
  facilityCode: string;
  username: string;
  password: string;
  serviceUrl: string;
  isActive: boolean;
}

export interface ApiAuditLog {
  id: number;
  roomId: number;
  action: string;
  description: string;
  performedBy: string;
  createdAt: string;
}

/* ==================== META (ORTAK SABİTLER) ==================== */

/** GET /api/meta/choices/ — roller, durumlar, kategoriler: { anahtar: [{value, label}] } */
export type MetaChoices = Schemas['MetaChoices'];

export const metaApi = {
  getChoices: () =>
    api.get<MetaChoices>('/meta/choices/').then((r) => r.data),
};

/* ==================== ROOMS API ==================== */

export const roomsApi = {
  getAll: () =>
    api.get<ApiRoom[]>('/rooms/').then((r) => r.data),

  getById: (id: number) =>
    api.get<ApiRoom>(`/rooms/${id}/`).then((r) => r.data),

  checkIn: (roomId: number, body: { guestId: number; notes?: string; checkOut?: string; companyId?: number; agencyId?: number; agencyReservationCode?: string }) =>
    api.post<ApiRoom>(`/rooms/${roomId}/check_in/`, body).then((r) => r.data),

  checkOut: (
    roomId: number,
    body?: {
      guestId?: number;
      force?: boolean;
      forceReason?: string;
      earlyDepartureMode?: 'refund' | 'full' | 'penalty';
      transferToCompany?: boolean;
      staffName?: string;
    }
  ) => api.post<ApiRoom>(`/rooms/${roomId}/check_out/`, body || {}).then((r) => r.data),

  addGuest: (roomId: number, guestId: number) =>
    api.post<ApiRoom>(`/rooms/${roomId}/add_guest/`, { guestId }).then((r) => r.data),

  updateStatus: (roomId: number, status: string) =>
    api.post<ApiRoom>(`/rooms/${roomId}/update_status/`, { status }).then((r) => r.data),

  updateNotes: (roomId: number, notes: string) =>
    api.post<ApiRoom>(`/rooms/${roomId}/update_notes/`, { notes }).then((r) => r.data),

  /** Odanın gecelik ücretini güncelle */
  updatePrice: (roomId: number, price: number) =>
    api.patch<ApiRoom>(`/rooms/${roomId}/`, { price: String(price) }).then((r) => r.data),

  /** Oda taşıma — misafirleri ve rezervasyonu hedef odaya aktar */
  moveGuests: (fromRoomId: number, toRoomId: number) =>
    api.post<ApiRoom>(`/rooms/${fromRoomId}/move/`, { toRoomId }).then((r) => r.data),

  /** Misafiri odadan çıkar */
  removeGuest: (roomId: number, guestId: number) =>
    api.post<ApiRoom>(`/rooms/${roomId}/remove_guest/`, { guestId }).then((r) => r.data),

  /** Checkout iptal — odayı tekrar occupied yap */
  /** Check-out geri al — kbsResendRequired: KBS'ye çıkış bildirilmişti, giriş tekrar gönderilmeli */
  revertCheckout: (roomId: number) =>
    api.post<ApiRoom & { kbsResendRequired: boolean; removedItems: string[] }>(
      `/rooms/${roomId}/revert_checkout/`,
    ).then((r) => r.data),

  create: (data: { roomNumber: string; bedType: string; floor: number; capacity: number; view: string; price: number; beds?: { type: string }[] }) =>
    api.post<ApiRoom>('/rooms/', {
      roomNumber: data.roomNumber,
      bedType: data.bedType,
      floor: data.floor,
      capacity: data.capacity,
      view: data.view,
      price: data.price,
      status: 'available',
      beds: data.beds || [],
    }).then((r) => r.data),

  update: (id: number, data: Record<string, unknown>) =>
    api.patch<ApiRoom>(`/rooms/${id}/`, data).then((r) => r.data),

  delete: (id: number) =>
    api.delete(`/rooms/${id}/`),
};

/* ==================== GUESTS API ==================== */

export const guestsApi = {
  getAll: () =>
    api.get<ApiGuest[]>('/guests/').then((r) => r.data),

  /** Sayfalı misafir listesi — search: ad / soyad / TC / pasaport / telefon (her kelime) */
  getPage: (query: PageQuery & { isBlocked?: boolean }) =>
    api.get<Paginated<ApiGuest>>('/guests/', { params: query }).then((r) => r.data),

  search: (q: string) =>
    api.get<ApiGuest[]>(`/guests/search/?q=${encodeURIComponent(q)}`).then((r) => r.data),

  create: (data: { tcNo: string; firstName: string; lastName: string; phone: string; email?: string; companyId?: number | null }) =>
    api.post<ApiGuest>('/guests/', data).then((r) => r.data),

  update: (id: number, data: Partial<ApiGuest>) =>
    api.put<ApiGuest>(`/guests/${id}/`, data).then((r) => r.data),

  toggleBlock: (id: number) =>
    api.post<ApiGuest>(`/guests/${id}/toggle_block/`).then((r) => r.data),

  stayHistory: (id: number) =>
    api.get<ApiReservation[]>(`/guests/${id}/stay_history/`).then((r) => r.data),

  /** TC ile müşteri kontrolü — kayıtlıysa bilgileri, blokeliyse uyarı döner */
  checkTc: (tc: string) =>
    api.get<{ found: boolean; isBlocked?: boolean; guest?: ApiGuest }>(`/guests/check_tc/?tc=${encodeURIComponent(tc)}`).then((r) => r.data),
};

/* ==================== COMPANIES API ==================== */

export const companiesApi = {
  getAll: () =>
    api.get<ApiCompany[]>('/companies/').then((r) => r.data),

  create: (data: { name: string; taxNumber?: string; address?: string; phone?: string; email?: string }) =>
    api.post<ApiCompany>('/companies/', data).then((r) => r.data),

  update: (id: number, data: Partial<ApiCompany>) =>
    api.put<ApiCompany>(`/companies/${id}/`, data).then((r) => r.data),

  delete: (id: number) =>
    api.delete(`/companies/${id}/`),

  getGuests: (id: number) =>
    api.get<ApiGuest[]>(`/companies/${id}/guests/`).then((r) => r.data),

  /** Borçlu firmalar listesi (bakiye > 0) */
  /** Cari bakiyesi > 0 olan firmalar (cari defter) */
  getDebtors: () =>
    api.get<CariDebtorRow[]>('/companies/debtors/').then((r) => r.data),
};

/* ==================== AGENCIES API ==================== */

export const agenciesApi = {
  getAll: () =>
    api.get<ApiAgency[]>('/agencies/').then((r) => r.data),

  create: (data: {
    name: string; taxNumber?: string; address?: string; phone?: string; email?: string;
    contactPerson?: string; commissionRate?: number; notes?: string;
  }) =>
    api.post<ApiAgency>('/agencies/', data).then((r) => r.data),

  update: (id: number, data: Partial<ApiAgency>) =>
    api.put<ApiAgency>(`/agencies/${id}/`, data).then((r) => r.data),

  delete: (id: number) =>
    api.delete(`/agencies/${id}/`),

  /** Acenteden gelen rezervasyonlar */
  getReservations: (id: number) =>
    api.get<any[]>(`/agencies/${id}/reservations/`).then((r) => r.data),

  /** Borçlu acenteler listesi (bakiye > 0) */
  /** Cari bakiyesi > 0 olan acenteler (cari defter) */
  getDebtors: () =>
    api.get<CariDebtorRow[]>('/agencies/debtors/').then((r) => r.data),
};

/* ==================== ACCOUNT TRANSACTIONS API ==================== */
/**
 * Cari hesap hareketleri — "Cariye Borçlu/Alacaklı Aktar" folio kayıtlarından
 * otomatik oluşur. Doğrudan create endpoint'i yoktur; yazma FolioItem üzerinden.
 */

export interface AccountTransactionFilter {
  company?: number;
  agency?: number;
  guest?: number;
  status?: 'open' | 'settled';
  type?: 'debit' | 'credit';
  targetType?: 'guest' | 'company_or_agency';
}

export const accountTransactionsApi = {
  getAll: (filters?: AccountTransactionFilter) => {
    const params = new URLSearchParams();
    if (filters?.company) params.append('company', String(filters.company));
    if (filters?.agency) params.append('agency', String(filters.agency));
    if (filters?.guest) params.append('guest', String(filters.guest));
    if (filters?.status) params.append('status', filters.status);
    if (filters?.type) params.append('type', filters.type);
    if (filters?.targetType) params.append('targetType', filters.targetType);
    const qs = params.toString();
    return api.get<ApiAccountTransaction[]>(`/account-transactions/${qs ? '?' + qs : ''}`).then((r) => r.data);
  },

  /** Açık cari hareketler + özet (kalan tutarlar). page verilirse sayfalı. */
  getDebtorList: (page?: { page: number; pageSize: number }) =>
    api.get<AccountTransactionDebtorListResponse & Partial<Paginated<ApiAccountTransaction>>>(
      '/account-transactions/debtor_list/', { params: page },
    ).then((r) => r.data),

  /** Borçlu listesi (kişi başı tek satır) — targetType: guest | company_or_agency */
  getDebtors: (targetType?: 'guest' | 'company_or_agency') =>
    api.get<CariDebtorsResponse>('/account-transactions/debtors/', { params: { targetType } }).then((r) => r.data),

  /** Bir borçlunun açık borç kutucukları (konaklama başına) */
  getOpenDebts: (filter: { company?: number; agency?: number; guest?: number }) =>
    api.get<CariOpenDebtsResponse>('/account-transactions/open_debts/', { params: filter }).then((r) => r.data),

  /** Cariye tahsilat (alacak): debitIds varsa önce onlar, yoksa en eski açık borçlar; fazlası avans kalır */
  collect: (data: CollectPayload) =>
    api.post<{ success: boolean; transaction: ApiAccountTransaction; balance: string }>(
      '/account-transactions/collect/', data,
    ).then((r) => r.data),

  /** Belirli borca tahsilat (kısmi olabilir). Rezervasyon foliosuna yazılmaz. */
  settle: (id: number, data: { amount?: number; paymentMethod: string; description?: string }) =>
    api.post<{ success: boolean; transactionId: number; status: string; remaining: string; balance: string }>(
      `/account-transactions/${id}/settle/`, data,
    ).then((r) => r.data),

  /** Tahsilatı iptal et (muhasebe / yönetim) — kapattığı borçlar tekrar açılır */
  cancelPayment: (id: number) =>
    api.post<{ success: boolean; balance: string }>(`/account-transactions/${id}/cancel_payment/`).then((r) => r.data),
};

/* ==================== MUHASEBE (ACCOUNTING) API ==================== */

export interface ApiExpense {
  id: number;
  category: string;
  categoryLabel: string;
  amount: string;
  date: string;
  description: string;
  vendor: string;
  paymentMethod: string;
  paymentMethodLabel: string;
  isRecurring: boolean;
  createdBy: string;
  createdAt: string;
}

export interface ExpensePayload {
  category: string;
  amount: number | string;
  date: string;
  description?: string;
  vendor?: string;
  paymentMethod?: string;
  isRecurring?: boolean;
  staffName?: string;
}

export interface CategoryTotal { category: string; total: string; }

export interface IncomeExpenseSummary {
  /** İndirimler düşülmeden önceki tahakkuk */
  grossIncome: string;
  discounts: string;
  /** Net gelir = grossIncome − discounts (Dashboard / Ciro raporlarıyla aynı) */
  income: string;
  expense: string;
  net: string;
  incomeByCategory: CategoryTotal[];
  expenseByCategory: CategoryTotal[];
}

export interface AccountingSummary {
  openDebit: string;
  openCredit: string;
  netReceivable: string;
  monthIncome: string;
  monthExpense: string;
  monthNet: string;
}

export interface GuestDebtor {
  id: number;
  name: string;
  phone: string;
  tcNo: string;
  debit: string;
  credit: string;
  balance: string;
}

export interface GuestDebtorsResponse {
  items: GuestDebtor[];
  count: number;
  totalBalance: string;
}

export const EXPENSE_CATEGORIES: { value: string; label: string }[] = [
  { value: 'rent', label: 'Kira' },
  { value: 'utilities', label: 'Elektrik/Su/Doğalgaz' },
  { value: 'salary', label: 'Personel/Maaş' },
  { value: 'tax', label: 'Vergi/SGK' },
  { value: 'maintenance', label: 'Bakım/Onarım' },
  { value: 'supplies', label: 'Malzeme/İkmal' },
  { value: 'food', label: 'Gıda/İçecek' },
  { value: 'marketing', label: 'Pazarlama' },
  { value: 'commission', label: 'Komisyon' },
  { value: 'other', label: 'Diğer' },
];

export const EXPENSE_PAYMENT_METHODS: { value: string; label: string }[] = [
  { value: 'cash', label: 'Nakit' },
  { value: 'card', label: 'Kart' },
  { value: 'transfer', label: 'Havale/EFT' },
  { value: 'other', label: 'Diğer' },
];

export const accountingApi = {
  // ── Gider (gelir-gider defteri) ──
  listExpenses: (filters?: { category?: string; dateFrom?: string; dateTo?: string; recurring?: boolean }) => {
    const params = new URLSearchParams();
    if (filters?.category) params.append('category', filters.category);
    if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters?.dateTo) params.append('dateTo', filters.dateTo);
    if (filters?.recurring) params.append('recurring', 'true');
    const qs = params.toString();
    return api.get<ApiExpense[]>(`/accounting/expenses/${qs ? '?' + qs : ''}`).then((r) => r.data);
  },
  /** Sayfalı gider listesi */
  listExpensesPage: (query: PageQuery & { category?: string; dateFrom?: string; dateTo?: string }) =>
    api.get<Paginated<ApiExpense>>('/accounting/expenses/', { params: query }).then((r) => r.data),
  createExpense: (payload: ExpensePayload) =>
    api.post<ApiExpense>('/accounting/expenses/', payload).then((r) => r.data),
  updateExpense: (id: number, payload: Partial<ExpensePayload>) =>
    api.put<ApiExpense>(`/accounting/expenses/${id}/`, payload).then((r) => r.data),
  deleteExpense: (id: number) =>
    api.delete(`/accounting/expenses/${id}/`).then((r) => r.data),

  // ── Özet / gelir-gider / şahıs carileri ──
  summary: () =>
    api.get<AccountingSummary>('/accounting/summary/').then((r) => r.data),
  incomeExpense: (filters?: { dateFrom?: string; dateTo?: string }) => {
    const params = new URLSearchParams();
    if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters?.dateTo) params.append('dateTo', filters.dateTo);
    const qs = params.toString();
    return api.get<IncomeExpenseSummary>(`/accounting/income-expense/${qs ? '?' + qs : ''}`).then((r) => r.data);
  },
  guestDebtors: () =>
    api.get<GuestDebtorsResponse>('/accounting/guest-debtors/').then((r) => r.data),
};

/* ==================== RESERVATIONS API ==================== */

export const reservationsApi = {
  /** Rezervasyon listesi (çoklu filtre desteği) */
  getAll: (filters?: {
    roomId?: number; guestId?: number; companyId?: number;
    isActive?: boolean; status?: string;
    dateFrom?: string; dateTo?: string;
    filter?: string; search?: string;
  }) => {
    const params = new URLSearchParams();
    if (filters?.roomId) params.append('roomId', String(filters.roomId));
    if (filters?.guestId) params.append('guestId', String(filters.guestId));
    if (filters?.companyId) params.append('companyId', String(filters.companyId));
    if (filters?.isActive !== undefined) params.append('isActive', String(filters.isActive));
    if (filters?.status) params.append('status', filters.status);
    if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters?.dateTo) params.append('dateTo', filters.dateTo);
    if (filters?.filter) params.append('filter', filters.filter);
    if (filters?.search) params.append('search', filters.search);
    const qs = params.toString();
    return api.get<ApiReservation[]>(`/reservations/${qs ? '?' + qs : ''}`).then((r) => r.data);
  },

  /** Sayfalı rezervasyon listesi — search: misafir adı / oda no */
  getPage: (query: PageQuery & { filter?: string; dateFrom?: string; dateTo?: string }) =>
    api.get<Paginated<ApiReservation>>('/reservations/', { params: query }).then((r) => r.data),

  /** Rezervasyon detayı (misafirler + folio) */
  getById: (id: number) =>
    api.get<ApiReservationDetail>(`/reservations/${id}/`).then((r) => r.data),

  /**
   * Yeni rezervasyon oluştur (check-in yapmadan).
   * Ya `guestId` ya da `placeholderGuestName` zorunlu.
   * Placeholder verilirse Guest oluşturulmaz, Stay yaratılmaz —
   * misafir geldiğinde NewGuestDialog'da placeholder bilgisi prefill edilir.
   */
  create: (data: {
    roomId: number;
    guestId?: number;
    placeholderGuestName?: string;
    placeholderGuestPhone?: string;
    checkIn: string; checkOut?: string;
    notes?: string; staffName?: string;
    companyId?: number;
    agencyId?: number;
    agencyReservationCode?: string;
  }) =>
    api.post<ApiReservation>('/reservations/', data).then((r) => r.data),

  /** Rezervasyon iptal */
  cancel: (id: number) =>
    api.post<ApiReservation>(`/reservations/${id}/cancel/`).then((r) => r.data),

  /** Rezervasyon güncelle */
  update: (id: number, data: {
    roomId?: number;
    checkIn?: string;
    checkOut?: string;
    notes?: string;
    companyId?: number | null;
    agencyId?: number | null;
    agencyReservationCode?: string;
  }) =>
    api.put<ApiReservation>(`/reservations/${id}/`, data).then((r) => r.data),

  /** Rezerve → Check-in dönüşümü */
  checkIn: (id: number, body?: { companyId?: number | null; agencyId?: number | null; agencyReservationCode?: string }) =>
    api.post<ApiReservation>(`/reservations/${id}/check_in/`, body || {}).then((r) => r.data),

  /** Check-in iptal — checked_in → reserved geri dön */
  revertCheckin: (id: number) =>
    api.post<ApiReservation>(`/reservations/${id}/revert_checkin/`).then((r) => r.data),
};

/* ==================== FOLIOS API ==================== */

export const foliosApi = {
  getForReservation: (reservationId: number) =>
    api.get<ApiFolioItem[]>(`/folios/?reservationId=${reservationId}`).then((r) => r.data),

  getForRoom: (roomId: number) =>
    api.get<ApiFolioItem[]>(`/folios/?roomId=${roomId}`).then((r) => r.data),

  /** category='payment' ise paymentMethod zorunlu (cash | card | transfer | other) */
  create: (data: { reservationId: number; guestId?: number | null; category: string; description: string; amount: number; date: string; paymentMethod?: string; createdBy?: string }) =>
    api.post<ApiFolioItem>('/folios/', data).then((r) => r.data),

  delete: (id: number) =>
    api.delete(`/folios/${id}/`),
};

/* ==================== REPORTS API ==================== */

export const reportsApi = {
  company: (id: number, filters?: { dateFrom?: string; dateTo?: string }) => {
    const params = new URLSearchParams();
    if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters?.dateTo) params.append('dateTo', filters.dateTo);
    const qs = params.toString();
    return api.get<any>(`/reports/company/${id}/${qs ? '?' + qs : ''}`).then((r) => r.data);
  },

  guest: (id: number, filters?: { dateFrom?: string; dateTo?: string }) => {
    const params = new URLSearchParams();
    if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters?.dateTo) params.append('dateTo', filters.dateTo);
    const qs = params.toString();
    return api.get<any>(`/reports/guest/${id}/${qs ? '?' + qs : ''}`).then((r) => r.data);
  },

  room: (id: number, filters?: { dateFrom?: string; dateTo?: string }) => {
    const params = new URLSearchParams();
    if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters?.dateTo) params.append('dateTo', filters.dateTo);
    const qs = params.toString();
    return api.get<any>(`/reports/room/${id}/${qs ? '?' + qs : ''}`).then((r) => r.data);
  },
};

/* ==================== KAZANÇ (GELİR) API ==================== */

export interface DashboardStats {
  occupancy: {
    totalRooms: number;
    occupiedRooms: number;
    availableRooms: number;
    dirtyRooms: number;
    maintenanceRooms: number;
    blockedRooms: number;
    occupancyRate: number;
    singleRooms: number;            // yatak tipi bazlı (eski)
    doubleRooms: number;            // yatak tipi bazlı (eski)
    singleOccupancyRooms?: number;  // bugün 1 misafirli oda sayısı
    doubleOccupancyRooms?: number;  // bugün 2+ misafirli oda sayısı
  };
  revenue: {
    dailyRevenue: number;
    monthlyRevenue: number;
    lastMonthRevenue: number;
    monthlyGrowthPercent: number;
    dailyPayments: number;
    monthlyPayments: number;
    monthlyBalance: number;
  };
  todayCheckins: { guest: string; room: string; time: string }[];
  todayCheckouts: { guest: string; room: string; time: string }[];
}

export interface NightAuditPreviewRoom {
  roomId: number;
  roomNumber: string;
  guestName: string;
  price: number;
  checkIn: string | null;
  nights: number;
  companyName: string | null;
  reservationId: number;
}

export interface NightAuditNoShowRoom {
  reservationId: number;
  roomId: number;
  roomNumber: string;
  guestName: string;
  checkIn: string | null;
  companyName: string | null;
}

export interface NightAuditPreviewResponse {
  date: string;
  alreadyProcessed: boolean;
  occupiedRooms: NightAuditPreviewRoom[];
  totalCharge: number;
  roomCount: number;
  noShowRooms: NightAuditNoShowRoom[];
  noShowCount: number;
}

export interface NightAuditExecuteResponse {
  date: string;
  processedRooms: number;
  skippedRooms: number;
  totalCharged: number;
  details: { roomNumber: string; guestName: string; amount: number; companyName: string | null }[];
  noShowCount: number;
  noShowCancelled: number;
}

/** Son çalışan gün sonu — otomatik mi manuel mi olduğu dahil */
export interface NightAuditLastRun {
  date: string;
  processedAt: string;
  processedBy: string;
  automatic: boolean;
  roomsCharged: number;
  totalCharged: number;
}

/** Otomatik gün sonu durumu */
export interface NightAuditScheduleResponse {
  /** Ayarlanan saat "HH:MM" — null ise otomatik kapalı */
  nightAuditTime: string | null;
  enabled: boolean;
  /** Bugünün gün sonu alındı mı */
  ranToday: boolean;
  /** Sıradaki otomatik çalışma (ISO) — kapalıysa null */
  nextRun: string | null;
  lastRun: NightAuditLastRun | null;
}

/** Kaydedilmiş gün sonu raporu — GET /api/kazanc/night-audit-reports/ */
export interface NightAuditReportItem {
  id: number;
  date: string;
  roomsCharged: number;
  totalCharged: number;
  noShowCount: number;
  noShowCancelled: number;
  processedBy: string;
  processedAt: string | null;
  /** Otomatik gün sonu (daemon) tarafından mı çalıştırıldı */
  automatic: boolean;
  details: {
    roomNumber: string;
    guestName: string;
    amount: number;
    nights?: number;
    companyName: string | null;
    note?: string;
  }[];
  noShowDetails: { roomNumber: string; guestName: string; reservationId: number; cancelled: boolean }[];
  occupancySnapshot: { occupied?: number; available?: number; total?: number; rate?: number };
}

export const kazancApi = {
  /** Dashboard özet istatistikler (doluluk + ciro + check-in/out) */
  dashboardStats: () =>
    api.get<DashboardStats>('/kazanc/dashboard-stats/').then((r) => r.data),

  /** Gelir detay (kategori + oda tipi bazlı) */
  revenueBreakdown: (filters?: { month?: string; dateFrom?: string; dateTo?: string }) => {
    const params = new URLSearchParams();
    if (filters?.month) params.append('month', filters.month);
    if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters?.dateTo) params.append('dateTo', filters.dateTo);
    const qs = params.toString();
    return api.get<any>(`/kazanc/revenue-breakdown/${qs ? '?' + qs : ''}`).then((r) => r.data);
  },

  /** Günlük özet */
  dailySummary: (date?: string) => {
    const qs = date ? `?date=${date}` : '';
    return api.get<any>(`/kazanc/daily-summary/${qs}`).then((r) => r.data);
  },

  /** Gün sonu önizleme — konaklayan odalar + no-show tespiti */
  nightAuditPreview: () =>
    api.get<NightAuditPreviewResponse>('/kazanc/night-audit-preview/').then((r) => r.data),

  /** Gün sonu uygula — oda ücretlerini folio'ya yaz + rapor kaydet */
  nightAuditExecute: (processedBy?: string) =>
    api.post<NightAuditExecuteResponse>('/kazanc/night-audit/', { processedBy }).then((r) => r.data),

  /** No-show rezervasyon iptal et */
  cancelNoShow: (reservationId: number) =>
    api.post<{ reservationId: number; status: string; roomNumber: string }>(
      '/kazanc/night-audit-cancel-noshow/', { reservationId }
    ).then((r) => r.data),

  /** Otomatik gün sonu durumu — saat + sıradaki çalışma + son çalışma */
  getNightAuditSchedule: () =>
    api.get<NightAuditScheduleResponse>('/kazanc/night-audit-schedule/').then((r) => r.data),

  /** Otomatik gün sonu saatini ayarla (null = kapat) */
  setNightAuditSchedule: (time: string | null) =>
    api.post<NightAuditScheduleResponse>(
      '/kazanc/night-audit-schedule/',
      { time }
    ).then((r) => r.data),

  /** Geçmiş gün sonu raporları (en yeni önce, en fazla 100) */
  nightAuditReports: (filters?: { dateFrom?: string; dateTo?: string }) => {
    const params = new URLSearchParams();
    if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters?.dateTo) params.append('dateTo', filters.dateTo);
    const qs = params.toString();
    return api.get<NightAuditReportItem[]>(`/kazanc/night-audit-reports/${qs ? '?' + qs : ''}`).then((r) => r.data);
  },

  /** Gelişmiş kazanç raporu (çoklu filtreleme) */
  advancedReport: (filters?: {
    dateFrom?: string; dateTo?: string;
    bedTypes?: string; companyOnly?: boolean; individualOnly?: boolean;
    categories?: string; includeDebtors?: boolean;
  }) => {
    const params = new URLSearchParams();
    if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters?.dateTo) params.append('dateTo', filters.dateTo);
    if (filters?.bedTypes) params.append('bedTypes', filters.bedTypes);
    if (filters?.companyOnly) params.append('companyOnly', 'true');
    if (filters?.individualOnly) params.append('individualOnly', 'true');
    if (filters?.categories) params.append('categories', filters.categories);
    if (filters?.includeDebtors) params.append('includeDebtors', 'true');
    const qs = params.toString();
    return api.get<any>(`/kazanc/advanced-report/${qs ? '?' + qs : ''}`).then((r) => r.data);
  },
};

/* ==================== STAFF (PERSONEL) API ==================== */

/**
 * Personel — backend EmployeeSerializer'dan üretilir (web ve mobil aynı tip).
 * Otel/oturum alanları sadece login/me yanıtında gelir → opsiyonel.
 * salary sadece yöneticiye ve kişinin kendisine döner → opsiyonel.
 */
export type ApiEmployee = Schemas['Employee']
  & Partial<Pick<Schemas['EmployeeSession'], 'hotelId' | 'hotelName' | 'branchCode' | 'enabledModules' | 'isBmsAdmin'>>;

/** POST /api/staff/login/ yanıtı (personel + otel + JWT çifti) */
export type ApiLoginResponse = Schemas['EmployeeLoginResponse'];

export interface ApiTaskAssignment {
  id: number;
  employeeId: number;
  employeeName: string;
  isCompleted: boolean;
  completedAt: string | null;
  note: string;
  assignedAt: string;
}

export interface ApiTask {
  id: number;
  title: string;
  description: string;
  createdById: number;
  createdByName: string;
  assigneeNames: string | null;
  assigneeCount: number;
  completedCount: number;
  priority: string;
  status: string;
  dueDate: string | null;
  createdAt: string;
  assignments?: ApiTaskAssignment[];
}

export const staffApi = {
  getAll: (filters?: { status?: string; role?: string; search?: string }) => {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.role) params.append('role', filters.role);
    if (filters?.search) params.append('search', filters.search);
    const qs = params.toString();
    return api.get<ApiEmployee[]>(`/staff/${qs ? '?' + qs : ''}`).then((r) => r.data);
  },

  getById: (id: number) =>
    api.get<ApiEmployee>(`/staff/${id}/`).then((r) => r.data),

  create: (data: {
    firstName: string; lastName: string; phone: string; password: string;
    staffNumber: string; hireDate: string; roles: string[]; salary?: number;
  }) =>
    api.post<ApiEmployee>('/staff/', data).then((r) => r.data),

  update: (id: number, data: Record<string, unknown>) =>
    api.patch<ApiEmployee>(`/staff/${id}/`, data).then((r) => r.data),

  delete: (id: number) =>
    api.delete(`/staff/${id}/`),

  resetPassword: (id: number) =>
    api.post<{ success: boolean; staffNumber: string; fullName: string; newPassword: string }>(`/staff/${id}/reset_password/`).then((r) => r.data),

  login: (data: { branchCode: string; staffNumber: string; password: string }) =>
    api.post<ApiLoginResponse>('/staff/login/', data).then((r) => r.data),

  /** Giriş yapmış personelin bilgisi — kimlik JWT'den belirlenir */
  me: () =>
    api.get<Schemas['EmployeeSession']>('/staff/me/').then((r) => r.data),

  roles: () =>
    api.get<{ value: string; label: string }[]>('/staff/roles/').then((r) => r.data),

  subordinates: (id: number) =>
    api.get<ApiEmployee[]>(`/staff/${id}/subordinates/`).then((r) => r.data),
};

/* ==================== TASKS (GÖREV) API ==================== */

export const tasksApi = {
  getAll: (filters?: { assignee?: number; createdBy?: number; status?: string; priority?: string }) => {
    const params = new URLSearchParams();
    if (filters?.assignee) params.append('assignee', String(filters.assignee));
    if (filters?.createdBy) params.append('createdBy', String(filters.createdBy));
    if (filters?.status) params.append('status', filters.status);
    if (filters?.priority) params.append('priority', filters.priority);
    const qs = params.toString();
    return api.get<ApiTask[]>(`/tasks/${qs ? '?' + qs : ''}`).then((r) => r.data);
  },

  getById: (id: number) =>
    api.get<ApiTask>(`/tasks/${id}/`).then((r) => r.data),

  create: (data: {
    title: string; description?: string; createdById: number;
    assigneeIds: number[]; priority?: string; dueDate?: string;
  }) =>
    api.post<ApiTask>('/tasks/', data).then((r) => r.data),

  complete: (id: number, data: { employeeId: number; note?: string }) =>
    api.post<ApiTask>(`/tasks/${id}/complete/`, data).then((r) => r.data),

  cancel: (id: number) =>
    api.post<ApiTask>(`/tasks/${id}/cancel/`).then((r) => r.data),
};

/* ==================== LEAVES (İZİN) API ==================== */

/** İzin kaydı — backend LeaveSerializer'dan üretilir (mobil ile aynı tip) */
export type ApiLeave = Schemas['Leave'];

export const leavesApi = {
  getAll: (filters?: { employeeId?: number; leaveType?: string; status?: string; dateFrom?: string; dateTo?: string }) => {
    const params = new URLSearchParams();
    if (filters?.employeeId) params.append('employeeId', String(filters.employeeId));
    if (filters?.leaveType) params.append('leaveType', filters.leaveType);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters?.dateTo) params.append('dateTo', filters.dateTo);
    const qs = params.toString();
    return api.get<ApiLeave[]>(`/leaves/${qs ? '?' + qs : ''}`).then((r) => r.data);
  },

  create: (data: {
    employeeId: number; leaveType: string; startDate: string; endDate: string;
    deductFromAnnual?: boolean; note?: string; approvedById?: number;
  }) =>
    api.post<ApiLeave>('/leaves/', data).then((r) => r.data),

  /** Onaylı izni (yönetici) ya da bekleyen talebi (sahibi/yönetici) iptal eder */
  cancel: (id: number) =>
    api.post<ApiLeave>(`/leaves/${id}/cancel/`).then((r) => r.data),

  /** Personelin kendi izin talebi — 'pending' oluşur, kişi token'dan */
  request: (data: { leaveType: string; startDate: string; endDate: string; note?: string; deductFromAnnual?: boolean }) =>
    api.post<ApiLeave>('/leaves/request/', data).then((r) => r.data),

  /** Bekleyen talebi onayla (patron/müdür) — bakiye ve çakışma yeniden kontrol edilir */
  approve: (id: number) =>
    api.post<ApiLeave>(`/leaves/${id}/approve/`).then((r) => r.data),

  /** Bekleyen talebi reddet (patron/müdür) */
  reject: (id: number) =>
    api.post<ApiLeave>(`/leaves/${id}/reject/`).then((r) => r.data),

  getForEmployee: (employeeId: number) =>
    api.get<ApiLeave[]>(`/staff/${employeeId}/leaves/`).then((r) => r.data),
};

export interface ApiAttendanceLog {
  date: string;
  status: 'present' | 'absent' | 'leave' | 'day_off' | string;
  checkInTime: string | null;
  checkOutTime: string | null;
  workedHours: string | null;
}

/** Mesai QR içerikleri — mod başına "mesai:<mod>:<otel token'ı>" */
export interface AttendanceQrCodes {
  auto: string;
  giris: string;
  cikis: string;
}

export const attendanceApi = {
  /** Otelin mesai QR içerikleri (sadece patron/müdür) */
  getQrCodes: () =>
    api.get<{ codes: AttendanceQrCodes }>('/staff/attendance/qr/').then((r) => r.data.codes),

  /** QR token'ını yenile — yazdırılmış eski QR'lar çalışmaz hale gelir */
  regenerateQrCodes: () =>
    api.post<{ codes: AttendanceQrCodes }>('/staff/attendance/qr/regenerate/').then((r) => r.data.codes),

  getForEmployee: (employeeId: number, dateFrom?: string, dateTo?: string) => {
    const params = new URLSearchParams();
    if (dateFrom) params.append('dateFrom', dateFrom);
    if (dateTo) params.append('dateTo', dateTo);
    const qs = params.toString();
    return api.get<ApiAttendanceLog[]>(`/staff/${employeeId}/attendance/${qs ? '?' + qs : ''}`).then((r) => r.data);
  },
};

/* ==================== STOCK API ==================== */

export const stockApi = {
  getAll: (category?: string) => {
    const qs = category ? `?category=${category}` : '';
    return api.get<ApiStockItem[]>(`/stock/${qs}`).then((r) => r.data);
  },

  create: (data: { name: string; category: string; unit: string; quantity: number; isMinibar?: boolean; minibarPrice?: number }) =>
    api.post<ApiStockItem>('/stock/', data).then((r) => r.data),

  update: (id: number, data: Partial<{ name: string; category: string; unit: string; quantity: number; isMinibar: boolean; minibarPrice: number }>) =>
    api.put<ApiStockItem>(`/stock/${id}/`, data).then((r) => r.data),

  delete: (id: number) =>
    api.delete(`/stock/${id}/`),
};

/* ==================== MINIBAR API ==================== */

export const minibarApi = {
  /** Aktif minibar ürünleri + stok bilgisi */
  getProducts: () =>
    api.get<ApiMinibarProduct[]>('/minibar/products/').then((r) => r.data),

  /** Odanın minibar içeriği */
  getRoomItems: (roomId: number) =>
    api.get<ApiMinibarRoomItem[]>(`/minibar/rooms/${roomId}/`).then((r) => r.data),

  /** Minibara ürün ekle */
  addToRoom: (roomId: number, data: { productId: number; quantity: number; staffName?: string }) =>
    api.post<ApiMinibarRoomItem>(`/minibar/rooms/${roomId}/add/`, data).then((r) => r.data),

  /** Minibardan çıkar (iade) */
  removeFromRoom: (roomId: number, data: { productId: number; quantity: number }) =>
    api.post(`/minibar/rooms/${roomId}/remove/`, data),

  /** Tüketim + folio charge */
  consume: (roomId: number, data: { productId: number; quantity: number; staffName?: string }) =>
    api.post<{ folioItem: ApiFolioItem }>(`/minibar/rooms/${roomId}/consume/`, data).then((r) => r.data),
};

/* ==================== ARIZA (FAULT) API ==================== */

/** Arıza kaydı — backend FaultSerializer'dan üretilir (mobil ile aynı tip) */
export type ApiFault = Schemas['Fault'];

/** Mobil: faultsApi (services/api.ts) ile aynı uç noktalar */
export const faultsApi = {
  /** status: virgülle birden fazla durum, ör. "open,in_progress" */
  getAll: (filters?: { status?: string }) =>
    api.get<ApiFault[]>('/faults/', { params: filters?.status ? { status: filters.status } : undefined }).then((r) => r.data),

  /** Sayfalı arıza listesi — search: oda no / açıklama / bildiren */
  getPage: (query: PageQuery & { status?: string }) =>
    api.get<Paginated<ApiFault>>('/faults/', { params: query }).then((r) => r.data),

  getById: (id: number) =>
    api.get<ApiFault>(`/faults/${id}/`).then((r) => r.data),

  /** Arıza bildir (multipart) — bildiren backend'de token'dan belirlenir */
  create: (data: { roomNumber: string; category: string; description: string; photos?: File[] }) => {
    const form = new FormData();
    form.append('room_number', data.roomNumber);
    form.append('category', data.category);
    form.append('description', data.description);
    (data.photos || []).forEach((file) => form.append('photos', file));
    return api.post<ApiFault>('/faults/', form, { headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data);
  },

  /** open → in_progress → resolved (teknik ekip/yönetici) — çözen backend'de token'dan */
  updateStatus: (id: number, status: string) =>
    api.patch<ApiFault>(`/faults/${id}/status/`, { status }).then((r) => r.data),

  uploadResolutionPhotos: (id: number, photos: File[]) => {
    const form = new FormData();
    photos.forEach((file) => form.append('photos', file));
    return api.post<ApiFault>(`/faults/${id}/resolution-photos/`, form, { headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data);
  },
};

/* ==================== AUDIT LOG API ==================== */

export const auditApi = {
  /** Denetim logu kaydet */
  create: (data: { roomId: number; action: string; description: string; performedBy: string }) =>
    api.post<ApiAuditLog>('/audit-logs/', data).then((r) => r.data),

  /** Odanın denetim loglarını getir */
  getByRoom: (roomId: number) =>
    api.get<ApiAuditLog[]>(`/rooms/${roomId}/audit-logs/`).then((r) => r.data),
};

/* ==================== KBS API ==================== */

export const kbsApi = {
  /** Odadaki misafirleri KBS'ye bildir */
  send: (roomId: number) =>
    api.post<ApiKbsSendResponse>(`/kbs/send/${roomId}/`).then((r) => r.data),

  /** Manuel KBS çıkışı */
  checkout: (roomId: number) =>
    api.post(`/kbs/checkout/${roomId}/`),

  /** KBS kayıt listesi */
  getRecords: (filters?: { status?: string; roomId?: number }) => {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.roomId) params.append('roomId', String(filters.roomId));
    const qs = params.toString();
    return api.get<ApiKbsRecord[]>(`/kbs/records/${qs ? '?' + qs : ''}`).then((r) => r.data);
  },

  /** KBS ayarlarını getir */
  getSettings: () =>
    api.get<ApiKbsSettings>('/kbs/settings/').then((r) => r.data),

  /** KBS ayarlarını güncelle */
  updateSettings: (data: Partial<ApiKbsSettings>) =>
    api.put<ApiKbsSettings>('/kbs/settings/', data).then((r) => r.data),
};

/* ==================== HOTEL API ==================== */

export const hotelApi = {
  /** Otel bilgilerini getir */
  get: () =>
    api.get<ApiHotel>('/hotel/').then((r) => r.data),

  /** Otel bilgilerini güncelle */
  update: (data: Partial<{
    name: string; address: string; phone: string; email: string; taxNumber: string;
    requirePaymentAtCheckin: boolean; companyExemptFromCheckinPayment: boolean;
    serviceOpen: boolean;
  }>) =>
    api.put<ApiHotel>('/hotel/', data).then((r) => r.data),

  /** Menü QR token'ını yenile (eski QR geçersiz olur) */
  regenerateMenuToken: () =>
    api.post<{ menuAccessToken: string }>('/hotel/menu-token/regenerate/').then((r) => r.data),

  /** Belge yükle */
  uploadDocument: (file: File, type: string) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);
    return api.post<ApiHotelDocument>('/hotel/documents/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((r) => r.data);
  },

  /** Belge sil */
  deleteDocument: (id: number) =>
    api.delete(`/hotel/documents/${id}/`),

  /** Görsel yükle */
  uploadImage: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<ApiHotelImage>('/hotel/images/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((r) => r.data);
  },

  /** Görsel sil */
  deleteImage: (id: number) =>
    api.delete(`/hotel/images/${id}/`),

  /** Otel logosu yükle (eski varsa silinir) */
  uploadLogo: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<{ logo: string | null }>('/hotel/logo/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((r) => r.data);
  },

  /** Otel logosunu kaldır */
  deleteLogo: () =>
    api.delete('/hotel/logo/'),

  /** Modül bilgilerini getir */
  getModules: () =>
    api.get('/hotel/modules/').then((r) => r.data),

  /** Modülleri güncelle */
  updateModules: (data: { enabledModules: string[] }) =>
    api.put('/hotel/modules/', data).then((r) => r.data),
};

/* ==================== MENÜ API ==================== */

export interface ApiMenuCategory {
  id: number;
  name: string;
  sortOrder: number;
  isActive: boolean;
  itemCount?: number;
  items?: ApiMenuItem[];
}

export interface ApiMenuItem {
  id: number;
  categoryId: number;
  categoryName: string;
  name: string;
  description: string;
  price: string;
  image: string | null;
  imageUrl: string | null;
  isAvailable: boolean;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
}

export const menuApi = {
  /** Kategori listesi */
  getCategories: () =>
    api.get<ApiMenuCategory[]>('/menu-categories/').then((r) => r.data),

  /** Kategori detayı (ürünlerle) */
  getCategoryById: (id: number) =>
    api.get<ApiMenuCategory>(`/menu-categories/${id}/`).then((r) => r.data),

  /** Kategori oluştur */
  createCategory: (data: { name: string; sortOrder?: number }) =>
    api.post<ApiMenuCategory>('/menu-categories/', data).then((r) => r.data),

  /** Kategori güncelle */
  updateCategory: (id: number, data: Partial<ApiMenuCategory>) =>
    api.patch<ApiMenuCategory>(`/menu-categories/${id}/`, data).then((r) => r.data),

  /** Kategori sil */
  deleteCategory: (id: number) =>
    api.delete(`/menu-categories/${id}/`),

  /** Ürün listesi */
  getItems: (filters?: { categoryId?: number; isAvailable?: boolean }) => {
    const params = new URLSearchParams();
    if (filters?.categoryId) params.append('categoryId', String(filters.categoryId));
    if (filters?.isAvailable !== undefined) params.append('isAvailable', String(filters.isAvailable));
    const qs = params.toString();
    return api.get<ApiMenuItem[]>(`/menu-items/${qs ? '?' + qs : ''}`).then((r) => r.data);
  },

  /** Ürün oluştur */
  createItem: (data: FormData) =>
    api.post<ApiMenuItem>('/menu-items/', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((r) => r.data),

  /** Ürün güncelle */
  updateItem: (id: number, data: FormData | Record<string, unknown>) => {
    const config = data instanceof FormData
      ? { headers: { 'Content-Type': 'multipart/form-data' } }
      : undefined;
    return api.patch<ApiMenuItem>(`/menu-items/${id}/`, data, config).then((r) => r.data);
  },

  /** Ürün sil */
  deleteItem: (id: number) =>
    api.delete(`/menu-items/${id}/`),

  /** Public menü (QR kod) */
  getPublicMenu: () =>
    api.get<ApiMenuCategory[]>('/menu/public/').then((r) => r.data),

  /** Public katalog (otel branding + kategoriler) — şube koduyla */
  getCatalog: (branchCode: string, token?: string) => {
    const qs = token ? `?t=${encodeURIComponent(token)}` : '';
    return api.get<ApiCatalogResponse>(`/menu/catalog/${branchCode}/${qs}`).then((r) => r.data);
  },

  /** Katalog için müsait odalar (sadece check-in olmuş) */
  getCatalogRooms: (branchCode: string, token?: string) => {
    const qs = token ? `?t=${encodeURIComponent(token)}` : '';
    return api.get<ApiCatalogRoom[]>(`/menu/catalog/${branchCode}/rooms/${qs}`).then((r) => r.data);
  },

  /** Katalog için aktif masalar (restoran/kafe) */
  getCatalogTables: (branchCode: string, token?: string) => {
    const qs = token ? `?t=${encodeURIComponent(token)}` : '';
    return api.get<ApiCatalogTable[]>(`/menu/catalog/${branchCode}/tables/${qs}`).then((r) => r.data);
  },

  /** Katalogtan sipariş ver */
  placeCatalogOrder: (branchCode: string, payload: CatalogOrderPayload, token?: string) => {
    const qs = token ? `?t=${encodeURIComponent(token)}` : '';
    return api.post<CatalogOrderResponse>(`/menu/catalog/${branchCode}/order/${qs}`, payload).then((r) => r.data);
  },
};

export interface ApiCatalogRoom {
  roomNumber: string;
}

export interface ApiCatalogTable {
  id: number;
  tableNumber: string;
  serviceAreaName: string;
}

export interface CatalogOrderItem {
  menuItemId: number;
  quantity: number;
  notes?: string;
}

export interface CatalogOrderPayload {
  roomNumber?: string;
  tableId?: number;
  paymentLocation?: 'cashier' | 'table';
  customerName: string;
  items: CatalogOrderItem[];
}

export interface CatalogOrderResponse {
  tabId: number;
  tabNo: string;
  roomNumber?: string;
  tableNumber?: string;
  customerName: string;
  itemCount: number;
  totalAmount: string;
  paymentLocation?: 'cashier' | 'table' | '';
  message: string;
}

export interface ApiCatalogItem {
  id: number;
  name: string;
  description: string;
  price: string;
  imageUrl: string | null;
}

export interface ApiCatalogCategory {
  id: number;
  name: string;
  items: ApiCatalogItem[];
}

export interface ApiCatalogResponse {
  hotel: {
    name: string;
    branchCode: string;
    logoUrl: string | null;
    serviceOpen?: boolean;
  };
  categories: ApiCatalogCategory[];
}

/* ==================== ADİSYON (TAB) API ==================== */

export interface ApiTabItem {
  id: number;
  menuItemId: number | null;
  stockItemId: number | null;
  menuItemName: string | null;
  description: string;
  quantity: number;
  unitPrice: string;
  totalPrice: string;
  createdAt: string;
}

export interface ApiTab {
  id: number;
  tabNo: string;
  reservationId: number | null;
  roomId: number | null;
  roomNumber: string | null;
  tableId: number | null;
  tableNumber: string | null;
  guestName: string;
  servicePoint: string;
  status: string;
  openedById: number | null;
  openedByName: string | null;
  closedByName: string | null;
  totalAmount: string;
  paymentMethod: string | null;
  paymentLocation?: 'cashier' | 'table' | '';
  notes: string;
  openedAt: string;
  closedAt: string | null;
  paidAt: string | null;
  items?: ApiTabItem[];
}

export const tabsApi = {
  /** Adisyon listesi */
  getAll: (filters?: {
    status?: string; roomId?: number; reservationId?: number; servicePoint?: string;
    dateFrom?: string; dateTo?: string;
  }) => {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.roomId) params.append('roomId', String(filters.roomId));
    if (filters?.reservationId) params.append('reservationId', String(filters.reservationId));
    if (filters?.servicePoint) params.append('servicePoint', filters.servicePoint);
    if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters?.dateTo) params.append('dateTo', filters.dateTo);
    const qs = params.toString();
    return api.get<ApiTab[]>(`/tabs/${qs ? '?' + qs : ''}`).then((r) => r.data);
  },

  /** Sayfalı adisyon listesi */
  getPage: (query: PageQuery & { status?: string; servicePoint?: string; dateFrom?: string; dateTo?: string }) =>
    api.get<Paginated<ApiTab>>('/tabs/', { params: query }).then((r) => r.data),

  /** Adisyon detayı (kalemlerle) */
  getById: (id: number) =>
    api.get<ApiTab>(`/tabs/${id}/`).then((r) => r.data),

  /** Yeni adisyon aç */
  create: (data: {
    roomId?: number; guestName: string;
    servicePoint: string; openedById?: number; notes?: string;
  }) =>
    api.post<ApiTab>('/tabs/', data).then((r) => r.data),

  /** Kalem ekle */
  addItem: (tabId: number, data: {
    menuItemId?: number; stockItemId?: number;
    description?: string; quantity: number; unitPrice: number; notes?: string;
  }) =>
    api.post<ApiTabItem>(`/tabs/${tabId}/add_item/`, data).then((r) => r.data),

  /** Kalem adeti güncelle */
  updateItem: (tabId: number, itemId: number, quantity: number) =>
    api.post<ApiTabItem>(`/tabs/${tabId}/update_item/`, { itemId, quantity }).then((r) => r.data),

  /** Kalem sil */
  removeItem: (tabId: number, itemId: number) =>
    api.post(`/tabs/${tabId}/remove_item/`, { itemId }).then((r) => r.data),

  /** Adisyonu kapat */
  close: (tabId: number, closedById?: number) =>
    api.post<ApiTab>(`/tabs/${tabId}/close/`, { closedById }).then((r) => r.data),

  /** Ödeme yap */
  pay: (tabId: number, paymentMethod: 'room_charge' | 'cash' | 'card', registerId?: number, roomId?: number) =>
    api.post<ApiTab>(`/tabs/${tabId}/pay/`, { paymentMethod, registerId, roomId }).then((r) => r.data),

  /** İptal */
  cancel: (tabId: number) =>
    api.post<ApiTab>(`/tabs/${tabId}/cancel/`).then((r) => r.data),

  /** Adisyon böl */
  split: (tabId: number, itemIds: number[], guestName?: string) =>
    api.post(`/tabs/${tabId}/split/`, { itemIds, guestName }).then((r) => r.data),

  /** İade */
  refund: (tabId: number, reason?: string) =>
    api.post<ApiTab>(`/tabs/${tabId}/refund/`, { reason }).then((r) => r.data),
};

/* ──────────────────────────────────────────────────────────
   SHIFT HANDOVER (Mesai Devir)
   ────────────────────────────────────────────────────────── */

export const shiftApi = {
  /** Tüm mesai kayıtları */
  getAll: (params?: { status?: string; date?: string }) =>
    api.get('/shifts/', { params }).then((r) => r.data),

  /** Sayfalı mesai geçmişi (en yeni önce) */
  getPage: (query: PageQuery & { status?: string }) =>
    api.get<Paginated<ShiftHandover>>('/shifts/', { params: query }).then((r) => r.data),

  /** Aktif mesaiyi getir */
  getActive: () =>
    api.get('/shifts/active/').then((r) => r.data),

  /** Mesai devret */
  create: (data: { fromEmployeeId: number; toEmployeeId: number; notes?: string }) =>
    api.post('/shifts/', data).then((r) => r.data),

  /** Mesai kapat — tutarlar backend'de mesai süresindeki ödemelerden hesaplanır */
  close: (id: number, data: { notes?: string }) =>
    api.post(`/shifts/${id}/close/`, data).then((r) => r.data),

  /** Mesai detayı */
  getById: (id: number) =>
    api.get(`/shifts/${id}/`).then((r) => r.data),
};

/* ==================== CAMERA API ==================== */

export interface ApiCamera {
  id: number;
  name: string;
  location: string;
  streamUrl: string;
  snapshotUrl?: string;
  status: 'online' | 'offline';
  type: string;
  order: number;
}

export const cameraApi = {
  /** Tüm kameraları getir */
  getAll: () =>
    api.get<ApiCamera[]>('/cameras/').then((r) => r.data),

  /** Kamera ekle */
  create: (data: { name: string; location: string; streamUrl: string; snapshotUrl?: string; type?: string; order?: number }) =>
    api.post<ApiCamera>('/cameras/', data).then((r) => r.data),

  /** Kamera güncelle */
  update: (id: number, data: Partial<ApiCamera>) =>
    api.put<ApiCamera>(`/cameras/${id}/`, data).then((r) => r.data),

  /** Kamera sil */
  delete: (id: number) =>
    api.delete(`/cameras/${id}/`),
};

/* ==================== RESTORAN / CAFE API ==================== */

/* --- Service Area --- */

export interface ApiServiceArea {
  id: number;
  name: string;
  areaType: string;
  isActive: boolean;
  hasKitchen: boolean;
  printerName: string;
  tableCount: number;
  createdAt: string;
}

export const serviceAreasApi = {
  getAll: () =>
    api.get<ApiServiceArea[]>('/service-areas/').then((r) => r.data),
  create: (data: { name: string; areaType: string; hasKitchen?: boolean }) =>
    api.post<ApiServiceArea>('/service-areas/', data).then((r) => r.data),
  update: (id: number, data: Partial<ApiServiceArea>) =>
    api.patch<ApiServiceArea>(`/service-areas/${id}/`, data).then((r) => r.data),
  delete: (id: number) =>
    api.delete(`/service-areas/${id}/`),
};

/* --- Table (Masa) --- */

export interface ApiTable {
  id: number;
  tableNumber: string;
  serviceAreaId: number;
  serviceAreaName: string;
  capacity: number;
  status: string;
  isActive: boolean;
  positionX: number;
  positionY: number;
  qrToken: string;
  currentTabId: number | null;
  currentTotal: string;
  createdAt: string;
  currentTab?: ApiTab;
}

export const tablesApi = {
  getAll: (filters?: { serviceAreaId?: number; status?: string }) => {
    const params = new URLSearchParams();
    if (filters?.serviceAreaId) params.append('serviceAreaId', String(filters.serviceAreaId));
    if (filters?.status) params.append('status', filters.status);
    const qs = params.toString();
    return api.get<ApiTable[]>(`/tables/${qs ? '?' + qs : ''}`).then((r) => r.data);
  },
  getById: (id: number) =>
    api.get<ApiTable>(`/tables/${id}/`).then((r) => r.data),
  create: (data: { tableNumber: string; serviceAreaId: number; capacity?: number }) =>
    api.post<ApiTable>('/tables/', data).then((r) => r.data),
  update: (id: number, data: Partial<ApiTable>) =>
    api.patch<ApiTable>(`/tables/${id}/`, data).then((r) => r.data),
  delete: (id: number) =>
    api.delete(`/tables/${id}/`),
  open: (id: number, data: { guestName?: string; openedById?: number }) =>
    api.post<ApiTab>(`/tables/${id}/open/`, data).then((r) => r.data),
  /** Masaya direkt ürün ekle (tab yoksa otomatik oluşur) */
  addItem: (tableId: number, data: { menuItemId: number; quantity: number; notes?: string; openedById?: number }) =>
    api.post(`/tables/${tableId}/add_item/`, data).then((r) => r.data),
  close: (id: number) =>
    api.post<ApiTable>(`/tables/${id}/close/`).then((r) => r.data),
  transfer: (id: number, toTableId: number) =>
    api.post(`/tables/${id}/transfer/`, { toTableId }).then((r) => r.data),
};

/* --- Kitchen / Order Items --- */

export interface ApiOrderItemStatus {
  id: number;
  tabItemId: number;
  itemDescription: string;
  itemQuantity: number;
  itemUnitPrice: string;
  tabId: number;
  tabNo: string;
  tableNumber: string | null;
  roomNumber: string | null;
  servicePoint: string;
  guestName: string;
  status: string;
  notes: string;
  sentToKitchenAt: string;
  startedAt: string | null;
  readyAt: string | null;
  servedAt: string | null;
  preparedByName: string | null;
}

export interface ApiKitchenSummary {
  pending: number;
  preparing: number;
  ready: number;
  served: number;
  cancelled: number;
  total: number;
}

export const kitchenApi = {
  getOrders: (filters?: { status?: string; serviceAreaId?: number; tabId?: number }) => {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.serviceAreaId) params.append('serviceAreaId', String(filters.serviceAreaId));
    if (filters?.tabId) params.append('tabId', String(filters.tabId));
    const qs = params.toString();
    return api.get<ApiOrderItemStatus[]>(`/order-items/${qs ? '?' + qs : ''}`).then((r) => r.data);
  },
  startPreparing: (id: number, preparedById?: number) =>
    api.post<ApiOrderItemStatus>(`/order-items/${id}/start/`, { preparedById }).then((r) => r.data),
  markReady: (id: number) =>
    api.post<ApiOrderItemStatus>(`/order-items/${id}/ready/`).then((r) => r.data),
  markServed: (id: number) =>
    api.post<ApiOrderItemStatus>(`/order-items/${id}/serve/`).then((r) => r.data),
  cancelItem: (id: number, reason?: string) =>
    api.post<ApiOrderItemStatus>(`/order-items/${id}/cancel/`, { reason }).then((r) => r.data),
  getSummary: () =>
    api.get<ApiKitchenSummary>('/order-items/kitchen-summary/').then((r) => r.data),
};

/* --- Cash Register (Kasa) --- */

export interface ApiCashRegister {
  id: number;
  name: string;
  registerType: string;
  serviceAreaId: number | null;
  serviceAreaName: string | null;
  status: string;
  openedByName: string | null;
  closedByName: string | null;
  openingBalance: string;
  closingBalance: string | null;
  openedAt: string | null;
  closedAt: string | null;
  createdAt: string;
  todayTotals: {
    cash: string;
    card: string;
    roomCharge: string;
    expense: string;
    collected?: string;   // bugün kasaya tahsil edilen
    pending?: string;     // açık masalardaki bekleyen toplam
    total: string;        // collected + pending
  };
}

export interface ApiCashTransaction {
  id: number;
  registerId: number;
  tabId: number | null;
  tabNo: string | null;
  transactionType: string;
  amount: string;
  description: string;
  processedByName: string | null;
  createdAt: string;
}

export interface ApiCashSummary {
  date: string;
  registerName: string;
  status: string;
  openingBalance: string;
  closingBalance: string | null;
  cashSales: string;
  cardSales: string;
  roomChargeSales: string;
  totalSales: string;
  expenses: string;
  expectedCash: string;
  actualCash: string | null;
  difference: string | null;
  transactionCount: number;
}

export const kasaApi = {
  getAll: () =>
    api.get<ApiCashRegister[]>('/cash-registers/').then((r) => r.data),
  create: (data: { name: string; registerType: string; serviceAreaId?: number }) =>
    api.post<ApiCashRegister>('/cash-registers/', data).then((r) => r.data),
  open: (id: number, data: { openingBalance: number; openedById?: number }) =>
    api.post<ApiCashRegister>(`/cash-registers/${id}/open/`, data).then((r) => r.data),
  close: (id: number, data: { closingBalance: number; closedById?: number }) =>
    api.post<ApiCashRegister>(`/cash-registers/${id}/close/`, data).then((r) => r.data),
  getSummary: (id: number) =>
    api.get<ApiCashSummary>(`/cash-registers/${id}/summary/`).then((r) => r.data),
  getTransactions: (id: number, filters?: { dateFrom?: string; dateTo?: string; type?: string }) => {
    const params = new URLSearchParams();
    if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters?.dateTo) params.append('dateTo', filters.dateTo);
    if (filters?.type) params.append('type', filters.type);
    const qs = params.toString();
    return api.get<ApiCashTransaction[]>(`/cash-registers/${id}/transactions/${qs ? '?' + qs : ''}`).then((r) => r.data);
  },
  addExpense: (id: number, data: { amount: number; description: string; processedById?: number }) =>
    api.post<ApiCashTransaction>(`/cash-registers/${id}/add-expense/`, data).then((r) => r.data),
};

/* --- QR Sipariş (Public, auth olmadan) --- */

export interface ApiQRMenu {
  table: { id: number; tableNumber: string; serviceArea: string };
  categories: Array<{
    id: number;
    name: string;
    items: Array<{
      id: number;
      name: string;
      description: string;
      price: string;
      imageUrl: string | null;
    }>;
  }>;
}

export const qrApi = {
  getMenu: (token: string) =>
    api.get<ApiQRMenu>(`/restaurant/qr/${token}/menu/`).then((r) => r.data),
  placeOrder: (token: string, data: { guestName?: string; items: Array<{ menuItemId: number; quantity: number; notes?: string }> }) =>
    api.post(`/restaurant/qr/${token}/order/`, data).then((r) => r.data),
  getStatus: (token: string) =>
    api.get(`/restaurant/qr/${token}/status/`).then((r) => r.data),
  /** branch: şube kodu — aynı oda numarası birden fazla otelde varsa zorunlu (QR adresindeki ?branch=) */
  getRoomMenu: (roomNumber: string, branch?: string) =>
    api.get(`/restaurant/qr/room/${roomNumber}/menu/`, { params: branch ? { branch } : undefined }).then((r) => r.data),
  placeRoomOrder: (roomNumber: string, data: { items: Array<{ menuItemId: number; quantity: number; notes?: string }> }, branch?: string) =>
    api.post(`/restaurant/qr/room/${roomNumber}/order/`, data, { params: branch ? { branch } : undefined }).then((r) => r.data),
};

/* ==================== COMMISSION API ==================== */

export interface CommissionSettingsData {
  isActive: boolean;
  minAmount: string;
  commissionRate: string;
}

export interface CommissionItem {
  id?: number;
  employeeId?: number;
  employeeName?: string;
  tabNo: string;
  tableNumber: string;
  tabTotal: string;
  commissionRate: string;
  commissionAmount: string;
  date: string;
}

export interface MyCommissionsData {
  totalEarned: string;
  totalSales: string;
  count: number;
  items: CommissionItem[];
}

/** Komisyon raporu — toplamlar ve personel özeti tüm kayıtlar üzerinden (backend CommissionReportSerializer) */
export type CommissionReport = Schemas['CommissionReport'];

export const commissionApi = {
  getSettings: () =>
    api.get<CommissionSettingsData>('/commission/settings/').then((r) => r.data),

  updateSettings: (data: Partial<CommissionSettingsData>) =>
    api.put<CommissionSettingsData>('/commission/settings/', data).then((r) => r.data),

  getList: (filters?: { employeeId?: number; dateFrom?: string; dateTo?: string }) => {
    const params = new URLSearchParams();
    if (filters?.employeeId) params.append('employeeId', String(filters.employeeId));
    if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters?.dateTo) params.append('dateTo', filters.dateTo);
    const qs = params.toString();
    return api.get<CommissionReport>(`/commission/list/${qs ? '?' + qs : ''}`).then((r) => r.data);
  },

  getMy: (staffNumber: string, filters?: { dateFrom?: string; dateTo?: string }) => {
    const params = new URLSearchParams({ staffNumber });
    if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters?.dateTo) params.append('dateTo', filters.dateTo);
    return api.get<MyCommissionsData>(`/commission/my/?${params.toString()}`).then((r) => r.data);
  },
};

/* ==================== BMS (Bina Yönetim Sistemi) ==================== */

export interface BmsDevice {
  id: number;
  room: number;
  roomNumber: string;
  name: string;
  deviceType: 'light' | 'hvac' | 'curtain' | 'power_meter';
  status: 'online' | 'offline' | 'error';
  currentState: Record<string, unknown>;
  lastSeen: string | null;
  createdAt: string;
}

export interface BmsAlert {
  id: number;
  device: number;
  deviceName: string;
  roomNumber: string;
  severity: 'info' | 'warning' | 'critical';
  message: string;
  isResolved: boolean;
  createdAt: string;
}

export interface BmsEnergyReading {
  id: number;
  room: number;
  roomNumber: string;
  timestamp: string;
  kwh: string;
  costEstimate: string;
}

export const bmsApi = {
  getDevices: () =>
    api.get<BmsDevice[]>('/bms/devices/').then((r) => r.data),

  getRoomDevices: (roomId: number) =>
    api.get<BmsDevice[]>(`/bms/rooms/${roomId}/devices/`).then((r) => r.data),

  sendCommand: (roomId: number, deviceId: number, command: Record<string, unknown>) =>
    api.post<BmsDevice>(`/bms/rooms/${roomId}/command/`, { deviceId, command }).then((r) => r.data),

  getRoomEnergy: (roomId: number) =>
    api.get<BmsEnergyReading[]>(`/bms/rooms/${roomId}/energy/`).then((r) => r.data),

  getAlerts: () =>
    api.get<BmsAlert[]>('/bms/alerts/').then((r) => r.data),
};

/* ==================== INVOICES API ==================== */

export interface ApiInvoiceItem {
  id: number;
  templateId: number | null;
  description: string;
  quantity: string;
  unitPrice: string;
  vatRate: number;
  total: string;
}

export interface ApiInvoice {
  id: number;
  invoiceNo: string;
  type: string;
  documentType: string;
  status: 'draft' | 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
  customerType: 'individual' | 'company';
  customerName: string;
  taxNumber: string;
  taxOffice: string;
  address: string;
  phone: string;
  email: string;
  reservationId: number | null;
  guestId: number | null;
  companyId: number | null;
  subtotal: string;
  vatAmount: string;
  hasAccommodationTax: boolean;
  accommodationTaxAmount: string;
  totalAmount: string;
  pdfUrl: string;
  errorMessage: string;
  issueDate: string;
  dueDate: string | null;
  notes: string;
  createdBy: string;
  createdAt: string;
  items: ApiInvoiceItem[];
}

export interface InvoiceCreatePayload {
  type: 'sales' | 'purchase' | 'return' | 'incoming' | 'outgoing';
  documentType?: 'e_archive' | 'e_invoice';
  customerType: 'individual' | 'company';
  customerName: string;
  taxNumber?: string;
  taxOffice?: string;
  address?: string;
  phone?: string;
  email?: string;
  reservationId?: number | null;
  guestId?: number | null;
  companyId?: number | null;
  issueDate: string;
  dueDate?: string | null;
  notes?: string;
  createdBy?: string;
  hasAccommodationTax?: boolean;
  items: Array<{
    templateId?: number | null;
    description: string;
    quantity: number;
    unitPrice: number;
    vatRate: number;
  }>;
}

/** GET /api/invoices/prepare/{roomId}/ yanıtı — backend InvoicePrepareResponseSerializer'dan üretilir */
export type InvoicePrepareResponse = Schemas['InvoicePrepareResponse'];

export interface ParasutSettingsPayload {
  clientId: string;
  clientSecret: string;
  username: string;
  password: string;
  companyId: string;
  accountId: string;
  isActive: boolean;
}

export const invoicesApi = {
  prepare: (roomId: number) =>
    api.get<InvoicePrepareResponse>(`/invoices/prepare/${roomId}/`).then((r) => r.data),

  create: (payload: InvoiceCreatePayload) =>
    api.post<ApiInvoice>('/invoices/', payload).then((r) => r.data),

  send: (id: number) =>
    api.post<ApiInvoice>(`/invoices/${id}/send/`).then((r) => r.data),

  checkStatus: (id: number) =>
    api.post<ApiInvoice>(`/invoices/${id}/check-status/`).then((r) => r.data),

  get: (id: number) =>
    api.get<ApiInvoice>(`/invoices/${id}/`).then((r) => r.data),

  list: (filters?: { type?: string; status?: string; dateFrom?: string; dateTo?: string }) =>
    api.get<ApiInvoice[]>('/invoices/', { params: filters }).then((r) => r.data),

  /** Sayfalı fatura listesi — search: fatura no / müşteri / VKN-TCKN */
  listPage: (query: PageQuery & { type?: string; status?: string; dateFrom?: string; dateTo?: string }) =>
    api.get<Paginated<ApiInvoice>>('/invoices/', { params: query }).then((r) => r.data),

  cancel: (id: number) =>
    api.post<ApiInvoice>(`/invoices/${id}/cancel/`).then((r) => r.data),

  getParasutSettings: () =>
    api.get<ParasutSettingsPayload>('/invoices/parasut-settings/').then((r) => r.data),

  updateParasutSettings: (payload: ParasutSettingsPayload) =>
    api.put<ParasutSettingsPayload>('/invoices/parasut-settings/', payload).then((r) => r.data),

  testParasutSettings: (payload: Omit<ParasutSettingsPayload, 'isActive' | 'accountId'> & { accountId?: string }) =>
    api
      .post<{ success: boolean; message: string }>('/invoices/parasut-settings/test/', payload)
      .then((r) => r.data),
};
